import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'
import { useHistory } from 'react-router-dom'

import {
  cancelAtHomeBooking,
  fetchAtHomeBookings,
  fetchAtHomeSlots,
  resendAtHomePaymentLink,
  rescheduleAtHomeBooking
} from 'api/atHome'
import Button from 'app/components/generic/Button'
import { navigationPaths } from 'app/config'
import constants from 'app/constants'
import { confirm, eventAlert } from 'app/helpers'

import CloseSessionDialog, { canClose } from './CloseSessionDialog'
import { bookingRow, fmtDate, fmtTime, todayLocal } from './format'
import AtHomeCrudPage from './shared/AtHomeCrudPage'
import { parentLinkOptions } from './shared/parentLink'
import { payLinkOptions } from './shared/payLink'

// Column names and order agreed with the owner, 2026-10-21: when it was
// booked, then who (category, teacher, parent, child), then the session, then
// payment and the rest. "Session date/time" is when the class happens;
// "Booked on/at" is when the booking was made -- the two must not read alike.
//
// The old shared "Grade" column showed the TIME's grade, which is "Any" on
// every prod booking (and the cell was blank: bookingRow never filled it). It
// is replaced by the child's class, the one the parent picked while booking.
const columnsFor = (rowsRef) => {
  // Parent name and phone open the parent's Alfred profile, as on the
  // Cancelled & rescheduled tab.
  const parentLink = (field) => parentLinkOptions(rowsRef, field)
  return [
    { name: 'bookedOnLabel', label: 'Booked on', options: { keepOrder: true } },
    { name: 'bookedAtLabel', label: 'Booked at', options: { keepOrder: true } },
    { name: 'categoryName', label: 'Category' },
    { name: 'teacherName', label: 'Teacher' },
    { name: 'parentName', label: 'Parent', options: parentLink('parentName') },
    {
      name: 'parentPhone',
      label: "Parent's phone (WhatsApp goes here)",
      options: parentLink('parentPhone')
    },
    { name: 'childName', label: 'Child' },
    { name: 'childGradeLabel', label: "Child's grade" },
    { name: 'schoolName', label: 'School' },
    { name: 'dateLabel', label: 'Session date', options: { keepOrder: true } },
    { name: 'timeLabel', label: 'Session time', options: { keepOrder: true } },
    { name: 'duration', label: 'Session length' },
    { name: 'kindLabel', label: 'Session type' },
    // Upcoming, Attended, Not Attended, or past and unmarked (close it).
    { name: 'attendanceStateLabel', label: 'Attendance' },
    { name: 'apartmentName', label: "Apartment (the child's home)" },
    { name: 'priceLabel', label: 'Fee charged (incl. GST)' },
    { name: 'paymentLabel', label: 'Fee payment' },
    {
      name: 'payment_link_url',
      label: "Parent's pay-now link",
      options: payLinkOptions
    },
    { name: 'sourceLabel', label: 'Booked by (app or team)' },
    { name: 'rescheduledLabel', label: 'Times moved so far' }
  ]
}

// Where a booking stands. Every non-cancelled booking is listed here,
// attended ones too, so a day's count does not drop as sessions are marked
// (owner, 2026-10-22); Attended lists the present ones on their own.
const attendanceState = (row) => {
  if (row.status === 'completed') return 'Attended'
  if (row.status === 'no_show') return 'Not Attended'
  if (canClose(row)) return 'Not marked — close it'
  return 'Upcoming'
}

/** Every trial booking that was not cancelled: upcoming, waiting for
 * payment, attended, no-shows, and past ones never marked (closed here). */
const AtHomeBookings = ({ kind = 'trial' }) => {
  const history = useHistory()
  // The rows as drawn, for the parent-link cells (customBodyRenderLite gets an index).
  const rowsRef = React.useRef([])
  const [cancelling, setCancelling] = React.useState(null)
  const [reason, setReason] = React.useState('')
  const [refreshKey, setRefreshKey] = React.useState(0)
  // Reschedule: the booking being moved, the open slots it may move to, the pick.
  const [moving, setMoving] = React.useState(null)
  const [options, setOptions] = React.useState(null)
  const [target, setTarget] = React.useState('')
  const [moveReason, setMoveReason] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [closing, setClosing] = React.useState(null)

  const openReschedule = async (row) => {
    setMoving(row)
    setTarget('')
    setMoveReason('')
    setOptions(null)
    try {
      const today = todayLocal()
      // `apartment` is sent, and that is the whole fix: the admin slots
      // endpoint has always accepted it, this call just never passed it, so
      // ops was shown every building's times and the server then REFUSED the
      // move (services/booking.py enforces same-apartment). The old copy below
      // admitted as much -- "times at other apartments are listed here too,
      // but a move to one is refused" -- which is a worse answer than simply
      // not offering them. Reported 2026-10-19.
      const slots = await fetchAtHomeSlots({
        status: 'open',
        kind,
        category: row.category,
        apartment: row.apartment,
        from: today
      })
      setOptions(
        slots
          .filter(
            (s) =>
              s.uuid !== row.slot &&
              s.categoryName === row.categoryName &&
              // Teacher booked then -- other than by this very session, which
              // is the one moving, so overlapping it is allowed.
              !(s.teacherBusyWith || []).some((id) => id !== row.slot)
          )
          .sort((a, b) =>
            `${a.date} ${a.start_time}`.localeCompare(
              `${b.date} ${b.start_time}`
            )
          )
      )
    } catch (error) {
      setOptions([])
    }
  }

  const doReschedule = async () => {
    if (!target) return
    setSaving(true)
    try {
      await rescheduleAtHomeBooking(moving.uuid, target, moveReason)
      eventAlert(
        'Session moved; the old time is back on offer. The parent is not told automatically.',
        constants.SUCCESS
      )
      setMoving(null)
      setRefreshKey((k) => k + 1)
    } catch (error) {
      // toast from request.js
    } finally {
      setSaving(false)
    }
  }

  // Schedules listed is its own page now; remember which kind to land on.
  const goCreateSlot = () => {
    setMoving(null)
    window.localStorage.setItem('atHome.schedules.tab', kind)
    history.push(navigationPaths.atHomeSchedules)
  }

  const fetchList = React.useCallback(
    async (range) =>
      // `kind` goes to the server: this tab draws one kind, and asking for
      // both meant fetching (and parsing) every other tab's rows to throw
      // them away here. `range` is the header's date window, applied the same
      // way -- both narrow the payload rather than the rendered table.
      {
        const list = (
          await fetchAtHomeBookings({ view: 'booked', kind }, range)
        ).map((b) => {
          const row = bookingRow(b)
          return { ...row, attendanceStateLabel: attendanceState(row) }
        })
        rowsRef.current = list
        return list
      },
    [kind]
  )

  const doCancel = async () => {
    // A link-sent row never held its time, so nothing goes back on offer when
    // it is dropped (api/admin.py reopens the slot only for a booked one).
    const linkOnly = cancelling.payment_status === 'link_sent'
    try {
      await cancelAtHomeBooking(cancelling.uuid, reason)
      eventAlert(
        linkOnly
          ? 'Request dropped and its pay-now link closed. No time was being held, and the parent is not told automatically.'
          : 'Session cancelled; its time is back on offer. The parent is not told automatically.',
        constants.SUCCESS
      )
      setCancelling(null)
      setReason('')
      setRefreshKey((k) => k + 1)
    } catch (error) {
      // toast from request.js
    }
  }

  const resendLink = async (row) => {
    try {
      await resendAtHomePaymentLink(row.uuid)
      eventAlert('Payment link sent again on WhatsApp', constants.SUCCESS)
    } catch (error) {
      // toast from request.js
    }
  }

  return (
    <>
      <AtHomeCrudPage
        subtitle="Every trial booking except cancelled ones, with whether the child attended."
        entityLabel="Booking"
        fetchList={fetchList}
        dateRange
        dateRangePlaceholder="All session dates"
        columns={columnsFor(rowsRef)}
        refreshKey={`${kind}-${refreshKey}`}
        rowActions={(row) => (
          <>
            {row.payment_status === 'link_sent' && (
              <Button size="small" onClick={() => resendLink(row)}>
                Resend link
              </Button>
            )}
            {canClose(row) && (
              <Button size="small" onClick={() => setClosing(row)}>
                Close session
              </Button>
            )}
            {row.status === 'booked' && (
              <Button size="small" onClick={() => openReschedule(row)}>
                Reschedule
              </Button>
            )}
            {!['no_show', 'completed'].includes(row.status) && (
              <Button size="small" onClick={() => setCancelling(row)}>
                Cancel
              </Button>
            )}
          </>
        )}
      />
      <CloseSessionDialog
        row={closing}
        onClose={() => setClosing(null)}
        onDone={() => {
          setClosing(null)
          setRefreshKey((k) => k + 1)
        }}
      />
      <Dialog
        open={Boolean(moving)}
        onClose={() => !saving && setMoving(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Move this session to another time</DialogTitle>
        <DialogContent>
          {moving && (
            <DialogContentText>
              {moving.childName}&apos;s {moving.categoryName} session is on{' '}
              {moving.dateLabel}, {moving.timeLabel} with {moving.teacherName}.
              Pick another listed {moving.categoryName} time below. It has to be
              at the same apartment — {moving.apartmentName}, so only times
              there are listed. A different teacher is fine. The fee stays as it
              is, and the time this session gives up goes back on offer. The
              parent is not messaged; tell them yourself.
            </DialogContentText>
          )}
          {options === null && (
            <DialogContentText>Finding free times…</DialogContentText>
          )}
          {options && options.length === 0 && (
            <DialogContentText>
              No other free {moving?.categoryName} times are listed.{' '}
              <Link component="button" variant="body1" onClick={goCreateSlot}>
                Add one under Schedules listed
              </Link>
              , then come back here.
            </DialogContentText>
          )}
          {options && options.length > 0 && (
            <FormControl variant="outlined" fullWidth margin="normal">
              <InputLabel id="at-home-move-to">Move it to</InputLabel>
              <Select
                labelId="at-home-move-to"
                label="Move it to"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                {options.map((s) => (
                  <MenuItem key={s.uuid} value={s.uuid}>
                    {fmtDate(s.date)}, {fmtTime(s.start_time)} · {s.teacherName}{' '}
                    · {s.apartmentName}
                    {s.grade ? ` · listed for Grade ${s.grade}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {options && options.length > 0 && (
            <>
              <TextField
                label="Why it is moving, in the parent's words (our record only)"
                value={moveReason}
                onChange={(e) => setMoveReason(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                margin="normal"
              />
              <DialogContentText variant="body2">
                Need a time that isn&apos;t listed?{' '}
                <Link component="button" variant="body2" onClick={goCreateSlot}>
                  Add it under Schedules listed
                </Link>{' '}
                first.
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setMoving(null)} disabled={saving}>
            Keep as is
          </MuiButton>
          <MuiButton
            onClick={doReschedule}
            color="primary"
            variant="contained"
            disabled={!target || saving}
          >
            {saving ? 'Moving…' : 'Move session'}
          </MuiButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(cancelling)}
        onClose={() => setCancelling(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {cancelling && cancelling.payment_status === 'link_sent'
            ? 'Drop this request and close its pay-now link?'
            : 'Cancel this booked session?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelling && cancelling.payment_status === 'link_sent'
              ? 'Nothing is booked yet on this one, so no time is being held and nothing goes back on offer. The pay-now link stops working.'
              : 'The time goes back on offer to other families. No money is returned here, and the parent is not messaged — tell them yourself.'}
          </DialogContentText>
          <TextField
            label="Why (optional) — our record only, the parent never sees it"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setCancelling(null)}>Keep it</MuiButton>
          <MuiButton onClick={doCancel} color="secondary" variant="contained">
            {cancelling && cancelling.payment_status === 'link_sent'
              ? 'Yes, drop it'
              : 'Yes, cancel it'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

AtHomeBookings.propTypes = { kind: PropTypes.oneOf(['trial', 'regular']) }

export default AtHomeBookings
